import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import NetworkStatus from './NetworkStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#059669', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5', '#f59e0b', '#3b82f6'];

const FinanceDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [expenseRecords, setExpenseRecords] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [currency, setCurrency] = useState('USD');
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [chartFilter, setChartFilter] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [pendingReceipts, setPendingReceipts] = useState(0);

  useEffect(() => {
    loadDashboard();
    loadSettings();
    loadPendingReceipts();
  }, [dateRange, currency]);

  const loadPendingReceipts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/finance/receipts/stats`);
      const data = await response.json();
      if (data.success) {
        setPendingReceipts(data.stats.pending_receipts || 0);
      }
    } catch (error) {
      console.error('Error loading receipt stats:', error);
    }
  };

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/finance/dashboard?start_date=${dateRange.start}&end_date=${dateRange.end}&currency=${currency}`
      );
      const data = await response.json();
      if (data.success) {
        setDashboardData(data);
        setIncomeRecords(data.recent_income || []);
        setExpenseRecords(data.recent_expenses || []);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
    setLoading(false);
  };

  const loadSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/finance/settings`);
      const data = await response.json();
      if (data.success) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadFilteredData = async (type, category) => {
    try {
      const endpoint = type === 'income' ? 'income' : 'expenses';
      const categoryParam = type === 'income' ? 'category' : 'category';
      const response = await fetch(
        `${BACKEND_URL}/api/finance/${endpoint}?start_date=${dateRange.start}&end_date=${dateRange.end}&${categoryParam}=${encodeURIComponent(category)}`
      );
      const data = await response.json();
      if (data.success) {
        if (type === 'income') {
          setIncomeRecords(data.income);
        } else {
          setExpenseRecords(data.expenses);
        }
        setChartFilter({ type, category });
      }
    } catch (error) {
      console.error('Error loading filtered data:', error);
    }
  };

  const handleChartClick = (data, type) => {
    if (data && data.name) {
      loadFilteredData(type, data.name);
    }
  };

  const clearFilter = () => {
    setChartFilter(null);
    loadDashboard();
  };

  const exportCSV = async (type) => {
    window.open(
      `${BACKEND_URL}/api/finance/reports/csv?report_type=${type}&start_date=${dateRange.start}&end_date=${dateRange.end}`,
      '_blank'
    );
  };

  const downloadPDFReport = () => {
    const startDate = new Date(dateRange.start);
    window.open(
      `${BACKEND_URL}/api/finance/reports/pdf?year=${startDate.getFullYear()}&month=${startDate.getMonth() + 1}`,
      '_blank'
    );
  };

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const emailReport = async () => {
    if (!emailAddress) return;
    setEmailSending(true);
    try {
      const startDate = new Date(dateRange.start);
      const response = await fetch(`${BACKEND_URL}/api/finance/reports/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailAddress,
          year: startDate.getFullYear(),
          month: startDate.getMonth() + 1,
          include_pdf: true,
          include_html: true
        })
      });
      const data = await response.json();
      if (data.success) {
        alert(`Report sent successfully to ${emailAddress}`);
        setShowEmailModal(false);
        setEmailAddress('');
      } else {
        alert('Failed to send report: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error sending report:', error);
      alert('Failed to send report');
    }
    setEmailSending(false);
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // KPI Cards
  const KPICards = () => {
    if (!dashboardData?.kpis) return null;
    const { kpis } = dashboardData;
    
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <p className="text-green-100 text-sm">Total Income</p>
          <p className="text-2xl font-bold">{formatCurrency(kpis.total_income)}</p>
          <p className="text-green-200 text-xs mt-1">{currency}</p>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white">
          <p className="text-red-100 text-sm">Total Expenses</p>
          <p className="text-2xl font-bold">{formatCurrency(kpis.total_expenses)}</p>
          <p className="text-red-200 text-xs mt-1">{currency}</p>
        </div>
        <div className={`bg-gradient-to-br ${kpis.net_position >= 0 ? 'from-emerald-500 to-emerald-600' : 'from-orange-500 to-orange-600'} rounded-xl p-4 text-white`}>
          <p className="text-white/80 text-sm">Net Position</p>
          <p className="text-2xl font-bold">{formatCurrency(kpis.net_position)}</p>
          <p className={`text-xs mt-1 ${kpis.cashflow_status === 'positive' ? 'text-green-200' : 'text-orange-200'}`}>
            {kpis.cashflow_status === 'positive' ? '📈 Positive' : '📉 Negative'}
          </p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
          <p className="text-amber-100 text-sm">Outstanding Invoices</p>
          <p className="text-2xl font-bold">{formatCurrency(kpis.outstanding_invoices)}</p>
          <p className="text-amber-200 text-xs mt-1">{kpis.outstanding_count} invoices</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-blue-100 text-sm">Cashflow Status</p>
          <p className="text-2xl font-bold capitalize">{kpis.cashflow_status}</p>
          <p className="text-blue-200 text-xs mt-1">Current period</p>
        </div>
      </div>
    );
  };

  // Charts Section
  const ChartsSection = () => {
    if (!dashboardData?.charts) return null;
    const { charts } = dashboardData;

    // Combine income and expenses over time for comparison
    const timeData = [];
    const allDates = new Set([
      ...charts.income_over_time.map(d => d.date),
      ...charts.expenses_over_time.map(d => d.date)
    ]);
    Array.from(allDates).sort().forEach(date => {
      const income = charts.income_over_time.find(d => d.date === date)?.amount || 0;
      const expense = charts.expenses_over_time.find(d => d.date === date)?.amount || 0;
      timeData.push({ date: date.slice(5), income, expense });
    });

    return (
      <div className="space-y-6">
        {/* Filter indicator */}
        {chartFilter && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
            <span className="text-blue-700">
              Filtering by: <strong>{chartFilter.type}</strong> → <strong>{chartFilter.category}</strong>
            </span>
            <button onClick={clearFilter} className="text-blue-600 hover:text-blue-800 font-medium">
              Clear Filter ✕
            </button>
          </div>
        )}

        {/* Row 1: Income vs Expenses Over Time */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">📈 Income vs Expenses Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Line type="monotone" dataKey="income" stroke="#059669" strokeWidth={2} name="Income" dot={{ fill: '#059669' }} />
              <Line type="monotone" dataKey="expense" stroke="#ef4444" strokeWidth={2} name="Expenses" dot={{ fill: '#ef4444' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Row 2: Income by Source & Expenses by Category */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Income by Source */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">💰 Income by Source</h3>
            <p className="text-xs text-gray-500 mb-2">Click a segment to filter</p>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={charts.income_by_source}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  onClick={(data) => handleChartClick(data, 'income')}
                  style={{ cursor: 'pointer' }}
                >
                  {charts.income_by_source.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Expenses by Category */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">📊 Expenses by Category</h3>
            <p className="text-xs text-gray-500 mb-2">Click a bar to filter</p>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={charts.expenses_by_category} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" stroke="#6b7280" fontSize={12} tickFormatter={(v) => `$${v}`} />
                <YAxis type="category" dataKey="name" stroke="#6b7280" fontSize={10} width={100} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar 
                  dataKey="value" 
                  fill="#ef4444" 
                  radius={[0, 4, 4, 0]}
                  onClick={(data) => handleChartClick(data, 'expense')}
                  style={{ cursor: 'pointer' }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Row 3: Payment Methods & Top Payers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Payment Methods */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">💳 Payment Methods</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={charts.payment_methods}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" fontSize={10} />
                <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Top 10 Payers */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">🏆 Top 10 Income Sources</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={charts.top_payers} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" stroke="#6b7280" fontSize={12} tickFormatter={(v) => `$${v}`} />
                <YAxis type="category" dataKey="name" stroke="#6b7280" fontSize={10} width={120} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="amount" fill="#059669" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  // Delete record handler
  const handleDeleteRecord = async (type, id) => {
    if (deleteConfirmText !== 'DELETE') return;
    
    setDeleting(true);
    try {
      const endpoint = type === 'income' ? 'income' : 'expenses';
      const response = await fetch(`${BACKEND_URL}/api/finance/${endpoint}/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setShowDeleteModal(null);
        setDeleteConfirmText('');
        loadDashboard();
      } else {
        alert('Failed to delete record: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting record:', error);
      alert('Failed to delete record');
    }
    setDeleting(false);
  };

  // Data Tables
  const DataTables = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
      {/* Income Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-green-600 px-4 py-3 flex justify-between items-center">
          <h3 className="font-semibold text-white">💰 Recent Income</h3>
          <button 
            onClick={() => setShowIncomeModal(true)}
            className="px-3 py-1 bg-white text-green-600 rounded text-sm font-medium hover:bg-green-50"
          >
            + Add Income
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Source</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Payer</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {incomeRecords.length === 0 ? (
                <tr><td colSpan="5" className="px-3 py-8 text-center text-gray-500">No income records found</td></tr>
              ) : (
                incomeRecords.map((record) => (
                  <tr 
                    key={record.id} 
                    className="hover:bg-green-50"
                  >
                    <td 
                      className="px-3 py-2 text-sm text-gray-600 cursor-pointer"
                      onClick={() => setSelectedItem({ type: 'income', data: record })}
                    >{formatDate(record.date)}</td>
                    <td 
                      className="px-3 py-2 text-sm text-gray-800 cursor-pointer"
                      onClick={() => setSelectedItem({ type: 'income', data: record })}
                    >{record.source_category}</td>
                    <td 
                      className="px-3 py-2 text-sm text-gray-600 truncate max-w-[120px] cursor-pointer"
                      onClick={() => setSelectedItem({ type: 'income', data: record })}
                    >{record.payer_name}</td>
                    <td 
                      className="px-3 py-2 text-sm font-semibold text-green-600 text-right cursor-pointer"
                      onClick={() => setSelectedItem({ type: 'income', data: record })}
                    >
                      {formatCurrency(record.amount)}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteModal({ type: 'income', id: record.id, name: record.payer_name, amount: record.amount });
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expense Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-red-600 px-4 py-3 flex justify-between items-center">
          <h3 className="font-semibold text-white">📉 Recent Expenses</h3>
          <button 
            onClick={() => setShowExpenseModal(true)}
            className="px-3 py-1 bg-white text-red-600 rounded text-sm font-medium hover:bg-red-50"
          >
            + Add Expense
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Category</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Paid To</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {expenseRecords.length === 0 ? (
                <tr><td colSpan="5" className="px-3 py-8 text-center text-gray-500">No expense records found</td></tr>
              ) : (
                expenseRecords.map((record) => (
                  <tr 
                    key={record.id} 
                    className="hover:bg-red-50"
                  >
                    <td 
                      className="px-3 py-2 text-sm text-gray-600 cursor-pointer"
                      onClick={() => setSelectedItem({ type: 'expense', data: record })}
                    >{formatDate(record.date)}</td>
                    <td 
                      className="px-3 py-2 text-sm text-gray-800 cursor-pointer"
                      onClick={() => setSelectedItem({ type: 'expense', data: record })}
                    >{record.category}</td>
                    <td 
                      className="px-3 py-2 text-sm text-gray-600 truncate max-w-[120px] cursor-pointer"
                      onClick={() => setSelectedItem({ type: 'expense', data: record })}
                    >{record.paid_to}</td>
                    <td 
                      className="px-3 py-2 text-sm font-semibold text-red-600 text-right cursor-pointer"
                      onClick={() => setSelectedItem({ type: 'expense', data: record })}
                    >
                      {formatCurrency(record.amount)}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteModal({ type: 'expense', id: record.id, name: record.paid_to, amount: record.amount });
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Delete Confirmation Modal
  const DeleteConfirmModal = () => {
    if (!showDeleteModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
          <div className="bg-red-600 px-6 py-4 rounded-t-xl">
            <h2 className="text-xl font-bold text-white">⚠️ Confirm Delete</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium mb-2">
                You are about to permanently delete this {showDeleteModal.type} record:
              </p>
              <div className="text-sm text-red-700">
                <p><strong>Name:</strong> {showDeleteModal.name}</p>
                <p><strong>Amount:</strong> {formatCurrency(showDeleteModal.amount)}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-gray-700 font-medium">
                To confirm deletion, type <span className="font-bold text-red-600">DELETE</span> below:
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder="Type DELETE to confirm"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-center font-mono tracking-widest"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(null);
                  setDeleteConfirmText('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteRecord(showDeleteModal.type, showDeleteModal.id)}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                  deleteConfirmText === 'DELETE' && !deleting
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {deleting ? 'Deleting...' : '🗑️ Delete Record'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Add Income Modal
  const AddIncomeModal = () => {
    const [formData, setFormData] = useState({
      date: new Date().toISOString().split('T')[0],
      amount: '',
      currency: 'USD',
      source_category: '',
      payer_name: '',
      payment_method: '',
      reference_id: '',
      notes: ''
    });
    const [saving, setSaving] = useState(false);
    const [receiptFile, setReceiptFile] = useState(null);
    const [uploadError, setUploadError] = useState('');

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        // Validate file type
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
          setUploadError('Only PDF, JPG, and PNG files are allowed');
          e.target.value = '';
          return;
        }
        // Validate file size (2MB)
        if (file.size > 2 * 1024 * 1024) {
          setUploadError('File size must be less than 2MB');
          e.target.value = '';
          return;
        }
        setReceiptFile(file);
        setUploadError('');
      }
    };

    const uploadReceipt = async () => {
      if (!receiptFile) return null;
      
      const uploadFormData = new FormData();
      uploadFormData.append('file', receiptFile);
      uploadFormData.append('record_type', 'income');
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/finance/upload-receipt`, {
          method: 'POST',
          body: uploadFormData
        });
        const data = await response.json();
        if (data.success) {
          return data.file_url;
        }
      } catch (error) {
        console.error('Error uploading receipt:', error);
      }
      return null;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSaving(true);
      
      try {
        // Upload receipt first if provided (optional for income)
        let attachment_url = null;
        if (receiptFile) {
          attachment_url = await uploadReceipt();
        }
        
        const response = await fetch(`${BACKEND_URL}/api/finance/income`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            amount: parseFloat(formData.amount),
            receipt_url: attachment_url
          })
        });
        const data = await response.json();
        if (data.success) {
          setShowIncomeModal(false);
          loadDashboard();
        }
      } catch (error) {
        console.error('Error creating income:', error);
      }
      setSaving(false);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="bg-green-600 px-6 py-4 rounded-t-xl">
            <h2 className="text-xl font-bold text-white">💰 Add Income</h2>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="USD">🇺🇸 USD - US Dollar</option>
                  <option value="EUR">🇪🇺 EUR - Euro</option>
                  <option value="GBP">🇬🇧 GBP - British Pound</option>
                  <option value="LRD">🇱🇷 LRD - Liberian Dollar</option>
                  <option value="SLL">🇸🇱 SLL - Sierra Leonean Leone</option>
                  <option value="GNF">🇬🇳 GNF - Guinean Franc</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Income Source *</label>
                <select
                  required
                  value={formData.source_category}
                  onChange={(e) => setFormData({ ...formData, source_category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select category...</option>
                  <option value="Program Sponsorships">Program Sponsorships</option>
                  <option value="Airtime / Advertising">Airtime / Advertising</option>
                  <option value="Donations">Donations</option>
                  <option value="Tithes & Offerings">Tithes & Offerings</option>
                  <option value="Partnerships">Partnerships</option>
                  <option value="Events">Events</option>
                  <option value="Grants">Grants</option>
                  <option value="Merchandise Sales">Merchandise Sales</option>
                  <option value="Equipment Rental">Equipment Rental</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payer Name *</label>
              <input
                type="text"
                required
                value={formData.payer_name}
                onChange={(e) => setFormData({ ...formData, payer_name: e.target.value })}
                placeholder="Client or donor name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
              <select
                required
                value={formData.payment_method}
                onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="">Select method...</option>
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Wire Transfer">Wire Transfer</option>
                <option value="Mobile Money - Orange">Mobile Money - Orange</option>
                <option value="Mobile Money - Lonestar">Mobile Money - Lonestar</option>
                <option value="Mobile Money - MTN">Mobile Money - MTN</option>
                <option value="Check">Check</option>
                <option value="Online Payment">Online Payment</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference / Transaction ID</label>
              <input
                type="text"
                value={formData.reference_id}
                onChange={(e) => setFormData({ ...formData, reference_id: e.target.value })}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Receipt (Optional)
                <span className="text-xs text-gray-500 ml-2">PDF, JPG, PNG - Max 2MB</span>
              </label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
              />
              {uploadError && <p className="text-red-500 text-xs mt-1">{uploadError}</p>}
              {receiptFile && <p className="text-green-600 text-xs mt-1">📎 {receiptFile.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowIncomeModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Income'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Add Expense Modal
  const AddExpenseModal = () => {
    const [formData, setFormData] = useState({
      date: new Date().toISOString().split('T')[0],
      amount: '',
      currency: 'USD',
      category: '',
      paid_to: '',
      payment_method: '',
      reference_id: '',
      notes: '',
      staff_type: ''
    });
    const [saving, setSaving] = useState(false);
    const [receiptFile, setReceiptFile] = useState(null);
    const [uploadError, setUploadError] = useState('');

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        // Validate file type
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
          setUploadError('Only PDF, JPG, and PNG files are allowed');
          e.target.value = '';
          return;
        }
        // Validate file size (2MB)
        if (file.size > 2 * 1024 * 1024) {
          setUploadError('File size must be less than 2MB');
          e.target.value = '';
          return;
        }
        setReceiptFile(file);
        setUploadError('');
      }
    };

    const uploadReceipt = async () => {
      if (!receiptFile) return null;
      
      const uploadFormData = new FormData();
      uploadFormData.append('file', receiptFile);
      uploadFormData.append('record_type', 'expense');
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/finance/upload-receipt`, {
          method: 'POST',
          body: uploadFormData
        });
        const data = await response.json();
        if (data.success) {
          return data.file_url;
        }
      } catch (error) {
        console.error('Error uploading receipt:', error);
      }
      return null;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      // Receipt is mandatory for expenses
      if (!receiptFile) {
        setUploadError('Receipt is required for all expenses');
        return;
      }
      
      setSaving(true);
      
      try {
        // Upload receipt first (mandatory for expenses)
        const attachment_url = await uploadReceipt();
        
        if (!attachment_url) {
          setUploadError('Failed to upload receipt. Please try again.');
          setSaving(false);
          return;
        }
        
        const response = await fetch(`${BACKEND_URL}/api/finance/expenses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            amount: parseFloat(formData.amount),
            attachment_url: attachment_url
          })
        });
        const data = await response.json();
        if (data.success) {
          setShowExpenseModal(false);
          loadDashboard();
        }
      } catch (error) {
        console.error('Error creating expense:', error);
      }
      setSaving(false);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="bg-red-600 px-6 py-4 rounded-t-xl">
            <h2 className="text-xl font-bold text-white">📉 Add Expense</h2>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="USD">🇺🇸 USD - US Dollar</option>
                  <option value="EUR">🇪🇺 EUR - Euro</option>
                  <option value="GBP">🇬🇧 GBP - British Pound</option>
                  <option value="LRD">🇱🇷 LRD - Liberian Dollar</option>
                  <option value="SLL">🇸🇱 SLL - Sierra Leonean Leone</option>
                  <option value="GNF">🇬🇳 GNF - Guinean Franc</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Select category...</option>
                  <option value="Salaries">Salaries</option>
                  <option value="Volunteer Payments">Volunteer Payments</option>
                  <option value="Purchases / Supplies">Purchases / Supplies</option>
                  <option value="Travel & Transport">Travel & Transport</option>
                  <option value="Fuel">Fuel</option>
                  <option value="Maintenance / Repairs">Maintenance / Repairs</option>
                  <option value="Internet / Starlink">Internet / Starlink</option>
                  <option value="Utilities">Utilities</option>
                  <option value="Training">Training</option>
                  <option value="Projects">Projects</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Paid To *</label>
              <input
                type="text"
                required
                value={formData.paid_to}
                onChange={(e) => setFormData({ ...formData, paid_to: e.target.value })}
                placeholder="Person or vendor name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                <select
                  required
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Select method...</option>
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Wire Transfer">Wire Transfer</option>
                  <option value="Mobile Money - Orange">Mobile Money - Orange</option>
                  <option value="Mobile Money - Lonestar">Mobile Money - Lonestar</option>
                  <option value="Mobile Money - MTN">Mobile Money - MTN</option>
                  <option value="Check">Check</option>
                  <option value="Online Payment">Online Payment</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                <select
                  value={formData.staff_type}
                  onChange={(e) => setFormData({ ...formData, staff_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="">General</option>
                  <option value="staff">Staff Salary</option>
                  <option value="volunteer">Volunteer Stipend</option>
                  <option value="vendor">Vendor Payment</option>
                  <option value="contractor">Contractor Payment</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
              <input
                type="text"
                value={formData.reference_id}
                onChange={(e) => setFormData({ ...formData, reference_id: e.target.value })}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Receipt * <span className="text-red-500 font-bold">(Required)</span>
                <span className="text-xs text-gray-500 ml-2">PDF, JPG, PNG - Max 2MB</span>
              </label>
              <div className={`border-2 border-dashed rounded-lg p-4 text-center ${uploadError ? 'border-red-400 bg-red-50' : receiptFile ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                />
                {uploadError && <p className="text-red-600 text-sm mt-2 font-medium">{uploadError}</p>}
                {receiptFile && <p className="text-green-600 text-sm mt-2">✅ {receiptFile.name} selected</p>}
                {!receiptFile && !uploadError && <p className="text-gray-500 text-xs mt-2">Upload a receipt to document this expense</p>}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowExpenseModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !receiptFile}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  receiptFile && !saving 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {saving ? 'Saving...' : 'Save Expense'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Item Detail Modal
  const ItemDetailModal = () => {
    if (!selectedItem) return null;
    const { type, data } = selectedItem;
    const isIncome = type === 'income';

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
          <div className={`${isIncome ? 'bg-green-600' : 'bg-red-600'} px-6 py-4 rounded-t-xl flex justify-between items-center`}>
            <h2 className="text-xl font-bold text-white">
              {isIncome ? '💰 Income Details' : '📉 Expense Details'}
            </h2>
            <button onClick={() => setSelectedItem(null)} className="text-white hover:text-gray-200">✕</button>
          </div>
          <div className="p-6 space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Date:</span>
              <span className="font-medium">{formatDate(data.date)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Amount:</span>
              <span className={`font-bold text-lg ${isIncome ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(data.amount)} {data.currency}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{isIncome ? 'Source:' : 'Category:'}</span>
              <span className="font-medium">{isIncome ? data.source_category : data.category}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{isIncome ? 'Payer:' : 'Paid To:'}</span>
              <span className="font-medium">{isIncome ? data.payer_name : data.paid_to}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Payment Method:</span>
              <span className="font-medium">{data.payment_method}</span>
            </div>
            {data.reference_id && (
              <div className="flex justify-between">
                <span className="text-gray-500">Reference:</span>
                <span className="font-medium">{data.reference_id}</span>
              </div>
            )}
            {data.notes && (
              <div className="pt-2 border-t">
                <span className="text-gray-500 text-sm">Notes:</span>
                <p className="text-gray-800 mt-1">{data.notes}</p>
              </div>
            )}
            {(data.attachment_url || data.receipt_url) && (
              <div className="pt-2 border-t">
                <span className="text-gray-500 text-sm">Receipt:</span>
                <a 
                  href={data.attachment_url || data.receipt_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`block mt-1 px-3 py-2 rounded-lg text-sm font-medium text-center ${isIncome ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
                >
                  📎 View Receipt
                </a>
              </div>
            )}
            <div className="pt-4 flex gap-2">
              <button
                onClick={() => setSelectedItem(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setSelectedItem(null);
                  setShowDeleteModal({ 
                    type: type, 
                    id: data.id, 
                    name: isIncome ? data.payer_name : data.paid_to, 
                    amount: data.amount 
                  });
                }}
                className="flex-1 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading && !dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading finance dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📊 Finance Dashboard</h1>
          <p className="text-gray-500">Track income, expenses, and financial health</p>
        </div>
        {pendingReceipts > 0 && (
          <a 
            href="/crm?module=receipts"
            className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors animate-pulse"
          >
            <span className="text-xl">🧾</span>
            <span className="font-medium">{pendingReceipts} scanned receipt{pendingReceipts > 1 ? 's' : ''} pending</span>
            <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">Review</span>
          </a>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">From:</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">To:</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Currency:</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
            >
              <option value="USD">🇺🇸 USD</option>
              <option value="EUR">🇪🇺 EUR</option>
              <option value="GBP">🇬🇧 GBP</option>
              <option value="LRD">🇱🇷 LRD</option>
              <option value="SLL">🇸🇱 SLL</option>
              <option value="GNF">🇬🇳 GNF</option>
            </select>
          </div>
          <div className="flex-1"></div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => exportCSV('income')}
              className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm font-medium"
            >
              📥 CSV Income
            </button>
            <button
              onClick={() => exportCSV('expenses')}
              className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-medium"
            >
              📥 CSV Expenses
            </button>
            <button
              onClick={downloadPDFReport}
              className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm font-medium"
            >
              📄 PDF Report
            </button>
            <button
              onClick={() => setShowEmailModal(true)}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              📧 Email Report
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <KPICards />

      {/* Charts */}
      <ChartsSection />

      {/* Data Tables */}
      <DataTables />

      {/* Modals */}
      {showIncomeModal && <AddIncomeModal />}
      {showExpenseModal && <AddExpenseModal />}
      {selectedItem && <ItemDetailModal />}
      
      {/* Email Report Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-blue-600 px-6 py-4 rounded-t-xl">
              <h2 className="text-xl font-bold text-white">📧 Email Finance Report</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Send the monthly finance report as both an HTML email and PDF attachment.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Email *
                </label>
                <input
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-700">
                  <strong>Report Period:</strong> {new Date(dateRange.start).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Includes: HTML summary + PDF attachment
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmailAddress('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={emailReport}
                  disabled={!emailAddress || emailSending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {emailSending ? 'Sending...' : 'Send Report'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal />

      {/* Network Status Indicator - Offline Support */}
      <NetworkStatus />
    </div>
  );
};

export default FinanceDashboard;
